import { ActionTree } from "vuex";
import { RootState } from "@/store";
import { PendingCommentEdit, PendingNewReply, PendingReplyEdit } from "@/types/document/pendingDocumentComments";
import storeHelper from "@/store/store-helper";
import { CommentReply } from "@/types/commentReply";
import { CommentType } from "@/enums";

//state type
export type PendingDocumentCommentsState = {
    commentEdits: PendingCommentEdit[];
    newReplies: PendingNewReply[];
    replyEdits: PendingReplyEdit[];
};

export function initialize(): PendingDocumentCommentsState {
    return {
        commentEdits: [],
        newReplies: [],
        replyEdits: []
    };
}

//initial state
const state: PendingDocumentCommentsState = initialize();

//getters for this store
const getters = {
    /**
     * The pending comment edits
     * @param state The state to read from
     */
    commentEdits(state: PendingDocumentCommentsState): PendingCommentEdit[] {
        return [...state.commentEdits];
    },

    /**
     * The pending new replies
     * @param state The state to read from
     */
    newReplies(state: PendingDocumentCommentsState): PendingNewReply[] {
        return [...state.newReplies];
    },

    /**
     * The pending reply edits
     * @param state The state to read from
     */
    replyEdits(state: PendingDocumentCommentsState): PendingReplyEdit[] {
        return [...state.replyEdits];
    },

    /**
     * Returns wether for a document there are pending changes regarding comments
     * @param documentID The id of the document for which to determine whether there are changes
     */
    hasPendingItems: (state: PendingDocumentCommentsState) => {
        return (documentID: string): boolean => {
            return (
                state.commentEdits.some(x => x.document.document_id === documentID) ||
                state.newReplies.some(x => x.document.document_id === documentID) ||
                state.replyEdits.some(x => x.document.document_id === documentID)
            );
        };
    }
};

// mutations for this store
const mutations = {
    /**
     * Set the comment edits
     * @param state The state to write to
     * @param commentEdits All pending comment edits that will be in the state
     */
    commentEdits(state: PendingDocumentCommentsState, commentEdits: PendingCommentEdit[]): void {
        state.commentEdits = [...commentEdits];
    },
    /**
     * Set the new replies
     * @param state The state to write to
     * @param newReplies All pending new replies that will be in the state
     */
    newReplies(state: PendingDocumentCommentsState, newReplies: PendingNewReply[]): void {
        state.newReplies = [...newReplies];
    },
    /**
     * Set the reply edits
     * @param state The state to write to
     * @param replyEdits All pending reply edits that will be in the state
     */
    replyEdits(state: PendingDocumentCommentsState, replyEdits: PendingReplyEdit[]): void {
        state.replyEdits = [...replyEdits];
    }
};

// actions for this store
const actions = {
    /**
     * Adds or updates a pending comment edit
     * @param context The context of the action
     * @param pendingCommentEdit The comment data of the pending comment edit
     */
    addUpdateCommentEdit(context: { commit; state }, pendingCommentEdit: PendingCommentEdit): void {
        let localCommentEdits: PendingCommentEdit[] = [...context.state.commentEdits];
        const index = localCommentEdits.findIndex(pce => pce.commentID === pendingCommentEdit.commentID);

        if (index === -1) localCommentEdits.push(pendingCommentEdit);
        else localCommentEdits[index] = pendingCommentEdit;

        context.commit("commentEdits", localCommentEdits);
    },

    /**
     * Removes a pending comment edit
     * @param context The context of the action
     * @param commentID The id of the comment to remove from the pending comment edits
     */
    removeCommentEdit(context: { commit; state }, commentID: number): void {
        context.commit(
            "commentEdits",
            context.state.commentEdits.filter(pce => pce.commentID !== commentID)
        );
    },

    /**
     * Adds or updates a pending new reply
     * @param context The context of the action
     * @param pendingNewReply The reply data of the pending new reply
     */
    addUpdateNewReplies(context: { commit; state }, pendingNewReply: PendingNewReply): void {
        let localNewReplies: PendingNewReply[] = [...context.state.newReplies];
        const index = localNewReplies.findIndex(pnr => pnr.commentID === pendingNewReply.commentID);

        if (index === -1) localNewReplies.push(pendingNewReply);
        else localNewReplies[index] = pendingNewReply;

        context.commit("newReplies", localNewReplies);
    },

    /**
     * Removes a pending new reply
     * @param context The context of the action
     * @param commentID The commentID of the pending new reply to remove. A comment can have only one pending new reply
     */
    removeNewReplies(context: { commit; state }, commentID: number): void {
        context.commit(
            "newReplies",
            context.state.newReplies.filter(pnr => pnr.commentID !== commentID)
        );
    },

    /**
     * Adds or updates a pending reply edit
     * @param context The context of the action
     * @param pendingReplyEdit The reply data of the pending reply edit
     */
    addUpdateReplyEdits(context: { commit; state }, pendingReplyEdit: PendingReplyEdit): void {
        let localReplyEdits: PendingReplyEdit[] = [...context.state.replyEdits];
        const index = localReplyEdits.findIndex(pre => pre.replyID === pendingReplyEdit.replyID);

        if (index === -1) localReplyEdits.push(pendingReplyEdit);
        else localReplyEdits[index] = pendingReplyEdit;

        context.commit("replyEdits", localReplyEdits);
    },

    /**
     * Removes a pending reply edit
     * @param context The context of the action
     * @param replyID The id of the reply to remove from the pending reply edits
     */
    removeReplyEdits(context: { commit; state }, replyID: number): void {
        context.commit(
            "replyEdits",
            context.state.replyEdits.filter(pre => pre.replyID !== replyID)
        );
    },

    /**
     * Removes a pending reply edit
     * @param context The context of the action
     * @param commentID The id of the comment for which to remove all pending reply edits
     */
    removeReplyEditsForComment(context: { commit; state }, commentID: number): void {
        context.commit(
            "replyEdits",
            context.state.replyEdits.filter(pre => pre.commentID !== commentID)
        );
    },

    /**
     * Saves pending changes
     * @param context The context of the action
     * @param documentID The id of document for which to save the pending changes
     */
    savePendingItems({ commit, state }, documentID: string): void {
        const commentEditsToSave: PendingCommentEdit[] = [...state.commentEdits].filter(x => x.document.document_id === documentID);
        const replyEditsToSave: PendingReplyEdit[] = [...state.replyEdits].filter(x => x.document.document_id === documentID);
        const newRepliesToSave: PendingNewReply[] = [...state.newReplies].filter(x => x.document.document_id === documentID);

        commentEditsToSave.forEach(x =>
            storeHelper.documentStore.editCommentAsync(
                x.document.document_id,
                x.document.version,
                x.commentID,
                x.commentText,
                x.anchor_id,
                x.anchor_text,
                x.anchor_type
            )
        );

        replyEditsToSave.forEach(x => {
            const commentReply: CommentReply = {
                comment_reply_id: x.replyID,
                text: x.replyText,
                created_by_user: null,
                comment_type: CommentType.Reviewer,
                created_date_time: null,
                is_reject_reason: false
            };
            storeHelper.documentStore.editCommentReplyAsync(x.commentID, commentReply);
        });

        newRepliesToSave.forEach(x => {
            const commentReply: CommentReply = {
                text: x.replyText,
                created_by_user: null,
                comment_type: x.commentType,
                created_date_time: null,
                is_reject_reason: false
            };
            storeHelper.documentStore.addCommentReplyAsync(x.commentID, commentReply);
        });

        const remainingCommentEdits = state.commentEdits.filter(x => x.document.document_id === documentID);
        const remainingNewReplies = state.newReplies.filter(x => x.document.document_id === documentID);
        const remainingReplyEdits = state.replyEdits.filter(x => x.document.document_id !== documentID);

        //remove items for the document from the store
        commit("commentEdits", remainingCommentEdits);
        commit("newReplies", remainingNewReplies);
        commit("replyEdits", remainingReplyEdits);
    }
};

// export the namespace
export const namespace = "pendingDocumentComments";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
